/* eslint-disable no-console */
import Cookies from "js-cookie";
import { send } from "@utils/fetch";
import store, { AppDispatch, GetAppState } from "@utils/redux/store";
import { captureException } from "@utils/sentry";
import isEmpty from "lodash/isEmpty";
import { isEU, isFromEmail } from "@utils/userSegment";
import { setSurveyNameSpace } from "./surveyNameSpace";
import { setSurveyAnswers, SurveyAnswersState } from "./surveyAnswers";
import { updateVisitorStatusState } from "./visitorStatus";
import { timestamp, timezone, toISO } from "src/utils/datetime";
import { ServerContextState } from "./serverContext";

export type UserDataState = {
  user_id?: string;
  mixpanel_distinct_id?: string;
  name?: string;
  email?: string;
  country?: string;

  vip?: boolean;

  weight?: number;
  heightFeet?: number;
  heightInch?: number;

  // Enum of surveys below
  surveyNameSpace?:
    | "personaSurveyAnswers"
    | "personaSurveyUS"
    | "personaSurveyNonUS"
    | "personaSurveyHM"
    | "purchaseSurveyAnswers"
    | "purchaseSurveyAnswersHM";

  personaSurveyAnswers?: SurveyAnswersState;
  personaSurveyUS?: SurveyAnswersState;
  personaSurveyNonUS?: SurveyAnswersState;
  personaSurveyHM?: SurveyAnswersState;

  purchaseSurveyAnswers?: SurveyAnswersState;
  purchaseSurveyAnswersHM?: SurveyAnswersState;

  zip_tax_state?: string;

  language?: string;

  growthPlanDuration?: number;

  access_code?: string;
  last_login_device_OS?: string;

  force_merge?: boolean;
  upsell?: {
    products: string[];
  };
};

/**
 * Action types
 */
export const UPDATE_USER_DATA = "userData/updateUserData";

interface UserDataAction {
  type: typeof UPDATE_USER_DATA;
  userData: Partial<UserDataState>;
}

/**
 * Action
 * Updates the store with new userdata
 */
export function updateUserData(userData: UserDataState): UserDataAction {
  return {
    type: UPDATE_USER_DATA,
    userData,
  };
}

/**
 * The Django API uses the `_userId` cookie for some purposes. This utility helps
 * us set the cookie whenever its updated.
 */
function setUserIdCookie(userId: string) {
  if (!userId) return;
  const { serverContext } = store.getState();
  const cookiedUserId = Cookies.get("_userId");
  if (cookiedUserId !== userId) {
    Cookies.set("_userId", userId, {
      domain: serverContext.session_cookie_domain,
    });
  }
}

/**
 * Thunk
 */
export function fetchAndInitializeExistingUser(userId: string) {
  return (dispatch: AppDispatch, getState: GetAppState) => {
    console.log(`loading => users/${userId}`);
    return send("POST", `/userdata/api/v3/user/data/`, { clientUserId: userId })
      .then((json) => {
        // Make sure the user has a userId field set in their userData.
        const userData = { ...json.data };
        if (!userData.user_id) userData.user_id = userId;
        if (!userData.mixpanel_distinct_id)
          userData.mixpanel_distinct_id = userId;

        dispatch(updateUserData(userData));

        setUserIdCookie(userData.user_id);
        const mainSurveyNameSpace =
          json.data?.surveyNameSpace ||
          (getState().geoLocation.country_code === "US"
            ? "personaSurveyUS"
            : "personaSurveyNonUS");
        const backendMainSurveyAnswers = json.data[mainSurveyNameSpace] || {};

        if (mainSurveyNameSpace) {
          dispatch(setSurveyNameSpace(mainSurveyNameSpace));
          if (!isEmpty(backendMainSurveyAnswers)) {
            dispatch(setSurveyAnswers(backendMainSurveyAnswers));
          }
        }
      })
      .catch((e: any) => {
        captureException(e, "UserDataLoad-Failed to fetch");
      });
  };
}

/**
 * Thunk
 */
export function initializeNewUser(serverContext: ServerContextState) {
  return (dispatch: AppDispatch, getState: GetAppState) => {
    const data = {
      mixpanel_distinct_id: serverContext.distinct_id,
      user_id: serverContext.user_id,
    };
    dispatch(updateUserData(data));

    setUserIdCookie(serverContext.user_id);

    const nameSpace =
      getState().geoLocation.country_code === "US"
        ? "personaSurveyUS"
        : "personaSurveyNonUS";
    if (nameSpace) dispatch(setSurveyNameSpace(nameSpace));
    return Promise.resolve(data);
  };
}

/**
 * Thunk
 * Generic save user data call, updates entire userdata object.
 */
export function setUserProperties(properties: Partial<UserDataState>) {
  return (dispatch: AppDispatch, getState: GetAppState) => {
    return submitUserDataUpdate(dispatch, getState, "save", properties);
  };
}

/**
 * Thunk
 * Updates the main survey answers.
 */
export function saveMainSurveyAnswers(
  surveyAnswers: SurveyAnswersState,
  {
    surveyNameSpace,
    dateKey = "mainSurveyCompleteDateISO",
    extraProps,
  }: {
    surveyNameSpace?: string;
    dateKey?: string;
    extraProps?: JsonObject;
  } = {}
) {
  return (dispatch: AppDispatch, getState: GetAppState) => {
    const state = getState();

    const timezoneName = timezone();
    const timestampString = `${timestamp()}[${timezone()}]`;

    const properties: JsonObject = {
      name: surveyAnswers.name || "",

      clientTimestamp: timestampString,
      timezone: timezoneName,
      isEUCitizen: isEU(state),
      [dateKey]: toISO(),

      surveyNameSpace: surveyNameSpace || state.surveyNameSpace,
      [surveyNameSpace || state.surveyNameSpace]: surveyAnswers,
      ...extraProps,
    };

    // NOTE(patrick): If we pass an empty string email to the back end, userdata merge
    //                will throw a 500 error.
    const email = surveyAnswers.email || "";
    if (email) {
      properties.email = email;
      properties.latestMixPanelAlias = email;
    }

    if (state.geoLocation.country_code != null && state.language != null) {
      properties.language = state.language;
      properties.country = state.geoLocation.country_code;
    }

    if (state.geoLocation.subdivision != null) {
      properties.subdivision = state.geoLocation.subdivision;
    }

    if (state.geoLocation?.postal_code) {
      properties.postalCode = state.geoLocation.postal_code;
    }

    if (surveyAnswers.gdprConsent != null) {
      properties.consent = surveyAnswers.gdprConsent;
    }
    return submitUserDataUpdate(
      dispatch,
      getState,
      email ? "saveMainSurvey" : "save",
      properties
    );
  };
}

export function saveSurveyAnswers(
  surveyAnswers: SurveyAnswersState,
  {
    surveyNameSpace,
    dateKey,
    extraProps,
  }: {
    surveyNameSpace: string;
    dateKey: string;
    extraProps?: JsonObject;
  }
) {
  return (dispatch: AppDispatch, getState: GetAppState) => {
    const state = getState();

    const properties: JsonObject = {
      [dateKey]: toISO(),

      [surveyNameSpace]: surveyAnswers,
      ...extraProps,
    };

    if (state.geoLocation.country_code != null && state.language != null) {
      properties.language = state.language;
      properties.country = state.geoLocation.country_code;
    }

    if (state.geoLocation.subdivision != null) {
      properties.subdivision = state.geoLocation.subdivision;
    }

    return submitUserDataUpdate(dispatch, getState, "save", properties);
  };
}

/**
 * Thunk
 */
export function saveProfile(properties: { email: string; name: string }) {
  return (dispatch: AppDispatch, getState: GetAppState) => {
    return submitUserDataUpdate(dispatch, getState, "saveProfile", properties);
  };
}

/**
 * Thunk
 */
export function saveCareersNewsletter(properties: JsonObject) {
  return (dispatch: AppDispatch, getState: GetAppState) => {
    return submitUserDataUpdate(
      dispatch,
      getState,
      "saveCareersNewsletter",
      properties
    );
  };
}

/**
 * Thunk
 * Saves the experiment the user is in.
 */
export function saveExperiment(properties: JsonObject) {
  return (dispatch: AppDispatch, getState: GetAppState) => {
    return submitUserDataUpdate(dispatch, getState, "experiment", properties);
  };
}

/**
 * Utility function used by the thunks to execute the api call. It requires
 * the dispatch function generated by the thunks to update the store and state
 * when the updated userData is received from the server.
 */
function submitUserDataUpdate(
  dispatch: AppDispatch,
  getState: GetAppState,
  saveType: string,
  properties: JsonObject
) {
  const { userData, serverContext } = getState();
  const oldUserId = userData.user_id;
  const payload = properties;
  payload.lastUpdated = Date.now();
  payload.clientUserId = serverContext.user_id;
  console.log(`saving => users/${userData.user_id}`, payload);
  return send("POST", `/userdata/api/v3/user/data/${saveType}/`, payload)
    .then((json) => {
      const newUserData = { ...json.data };
      if (json?.user_id) newUserData.user_id = json.user_id;
      if (json?.distinct_id)
        newUserData.mixpanel_distinct_id = json.distinct_id;
      dispatch(updateUserData(newUserData));

      // Only update the store if we receive new information about the user
      if (oldUserId !== newUserData.user_id) {
        // In the case where the backend user was freshly created, then we do not learn anything
        // new about the user. Therefore, only fire off tracking event if not new user.
        // The exception is when the user is on an email route. In that case, treat any new
        // user created events as new information
        // This prevents multiple unnecessary visitorState tracking events
        const isNewUser = json?.is_new_user || false;
        if (!isNewUser || isFromEmail()) {
          dispatch(
            updateVisitorStatusState({
              user_id: newUserData.user_id,
              is_new_user: isNewUser,
            })
          );
        }
      }

      setUserIdCookie(json?.user_id || newUserData.user_id);

      console.log("Got user_id from server", json.user_id);
      console.log("Got distinct_id from server", json.distinct_id);
    })
    .catch((e: any) => {
      captureException(e, "UserDataSave-Failed to fetch");
    });
}

function userDataReducer(
  state: UserDataState,
  action: UserDataAction
): UserDataState {
  switch (action.type) {
    case UPDATE_USER_DATA:
      return { ...state, ...action.userData };
    default:
      return state || {};
  }
}

export default userDataReducer;
